import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ESTATUS_DE_TAREA, INTERVALO_ESTATUS_MS } from 'api/procesosMasivos/descuentosMasivos';

/**
 * Consulta periódicamente el estatus de una tarea en segundo plano hasta que termina o falla.
 * Encadena las consultas (no usa setInterval) para no tener dos peticiones en curso.
 * @param {(taskId: string) => Promise<{estatus: string, data?: object, error?: string}>} getEstatus
 * @param {number} [intervalo]
 */
const useEstatusDeTarea = (getEstatus, intervalo = INTERVALO_ESTATUS_MS) => {
  const [taskId, setTaskId] = useState(null);
  const [estatus, setEstatus] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const activoRef = useRef(false);

  const detener = useCallback(() => {
    activoRef.current = false;
    clearTimeout(timeoutRef.current);
  }, []);

  const consultar = useCallback(async (id) => {
    if (!activoRef.current) return;
    const respuesta = await getEstatus(id);
    // Pudo detenerse o reiniciarse mientras esperaba la respuesta
    if (!activoRef.current) return;
    if (respuesta.estatus === ESTATUS_DE_TAREA.PROCESANDO) {
      timeoutRef.current = setTimeout(() => consultar(id), intervalo);
      return;
    }
    activoRef.current = false;
    setEstatus(respuesta.estatus);
    if (respuesta.estatus === ESTATUS_DE_TAREA.TERMINADO) {
      setResultado(respuesta.data);
    } else {
      setError(respuesta.error);
    }
  }, [getEstatus, intervalo]);

  const iniciar = useCallback((id) => {
    detener();
    setTaskId(id);
    setEstatus(ESTATUS_DE_TAREA.PROCESANDO);
    setResultado(null);
    setError(null);
    activoRef.current = true;
    timeoutRef.current = setTimeout(() => consultar(id), intervalo);
  }, [consultar, detener, intervalo]);

  const reiniciar = useCallback(() => {
    detener();
    setTaskId(null);
    setEstatus(null);
    setResultado(null);
    setError(null);
  }, [detener]);

  useEffect(() => detener, [detener]);

  return {
    taskId,
    estatus,
    resultado,
    error,
    procesando: estatus === ESTATUS_DE_TAREA.PROCESANDO,
    iniciar,
    reiniciar,
  };
};

export default useEstatusDeTarea;
